import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, Sparkles, BookOpen, CheckCircle2, ArrowRight, Stars } from 'lucide-react'

interface CreationProgressProps {
  status: 'generating' | 'completed' | 'failed'
  progress: number
  currentStep: string
  onContinue?: () => void
  onRetry?: () => void
}

const CreationProgress = ({ 
  status, 
  progress, 
  currentStep,
  onContinue,
  onRetry 
}: CreationProgressProps) => {
  return (
    <Card className="p-12 bg-white/50 backdrop-blur-sm border-primary/10">
      <div className="max-w-xl mx-auto text-center">
        {status === 'generating' && (
          <div className="space-y-8">
            <div className="flex justify-center">
              <div className="relative">
                <Loader2 className="w-16 h-16 text-primary animate-spin" />
                <Sparkles className="w-8 h-8 text-accent absolute -top-2 -right-2 animate-pulse" />
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-gray-800">正在创作中...</h3>
              <p className="text-base text-gray-600">{currentStep}</p>
            </div>
            <div className="space-y-4">
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary via-accent to-secondary transition-all duration-500 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500">
                预计需要1-2分钟，请耐心等待
              </p>
            </div>
          </div>
        )}

        {status === 'completed' && (
          <div className="space-y-8">
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-green-500" />
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500 absolute -top-2 -right-2" />
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-gray-800">创作完成！</h3>
              <p className="text-base text-gray-600">
                你的绘本已经准备就绪，快来发布与大家分享吧
              </p>
            </div>
            {onContinue && (
              <div className="flex justify-end">
                <Button
                  onClick={onContinue}
                  className="flex items-center gap-2 bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90 px-6 py-5 text-base"
                >
                  <span>前往发布</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        )}

        {status === 'failed' && (
          <div className="space-y-8">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-gray-800">创作遇到问题</h3>
              <p className="text-base text-gray-600">
                请稍后重试或联系客服获取帮助
              </p>
            </div>
            {onRetry && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={onRetry}
                  className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <span>重新尝试</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

export default CreationProgress 